import React, { Component, Fragment } from 'react';
import { ButtonTheme, FileUploaderStatus, ModalButtonType, ModalType, Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { arrayOf, bool, eventListener, expression, extendComponent, list, number, object, oneOf, record, shape, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { AbortableClearEvent, AbortableCloseEvent, ChangeEvent, ClearEvent, CloseEvent, FocusEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { createAbort, enumValues, isEqualShallow, isFunction, isXSmallResolution, noop } from '@philips-internal/oswald-ui-utils';
import { FileService } from '../../services/file-service/file-service.js';
import { ToastService } from '../../services/toast-service/toast-service.js';
import { FieldContext } from '../field/context/field-context.js';
import { File as FileComponent } from '../file/file.js';
import { maxFileSizeShape } from '../file-uploader/file-uploader.js';
import { ContextualBar } from '../heading/contextual-bar/contextual-bar.js';
import { Modal } from '../modal/modal.js';
import { FileUploaderActions } from './components/file-uploader-actions.js';
import { FileUploaderContentMode } from './file-uploader-content-types.js';
import UploadArea from './upload-area.js';
import './file-uploader-content.css';
class FileUploaderContent extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            files: new Map(),
        };
        /**
         * Calls the onCancel method provided in the given fileItem.
         *
         * @param fileItem the given fileItem
         */
        this.onCancel = ({ onCancel }) => {
            if (isFunction(onCancel)) {
                onCancel();
            }
        };
        /**
         * Fires events onBeforeClose and onClose.
         */
        this.onClose = () => {
            const { onBeforeClose, onClose, onRequiredFileError } = this.props;
            if (isFunction(onBeforeClose)) {
                onBeforeClose(new AbortableCloseEvent({ abort: noop }));
            }
            if (isFunction(onRequiredFileError)) {
                onRequiredFileError(this.hasMissingFiles());
            }
            if (isFunction(onClose)) {
                onClose(new CloseEvent());
            }
        };
        /**
         * Validates file description fires onBlur event
         */
        this.onBlur = () => {
            const { onBlur } = this.props;
            this.validateDescription();
            if (isFunction(onBlur)) {
                onBlur(new FocusEvent());
            }
        };
        /**
         * Updates the description touch state and fires onFocus
         *
         * @param fileItem the FileItem that originates the event
         */
        this.onFocus = (fileItem) => {
            const { onFocus } = this.props;
            const { files } = this.state;
            const updatedFileItem = { ...fileItem, descriptionTouched: true };
            files.set(fileItem.key, updatedFileItem);
            this.setState({ files }, () => {
                if (isFunction(onFocus)) {
                    onFocus(new FocusEvent());
                }
            });
        };
        /**
         * Delegates the preview operation to @see FileService.preview sending to it the given path.
         *
         * @param path the given path
         */
        this.onPreview = (path) => {
            const { fileHandlerInstance, onPreview } = this.props;
            fileHandlerInstance.preview(path);
            if (isFunction(onPreview)) {
                onPreview({ path });
            }
        };
        /**
         * Updates state of the component according the given response progress values (path, percentage and state).
         * Notify observers regarding state changes in the given FileItem (delegates to @see FileUploaderContent.notififyFileItemStatus).
         *
         * @param response received from the backend service
         * @param fileItem the FileItem that originates the upload request
         */
        this.onProgress = (response, fileItem) => {
            const { path, percentage, state } = response.uploadProgress;
            const { files } = this.state;
            const changes = {
                onCancel: response.onCancel,
                path,
                percentage,
                status: state,
            };
            const updatedFileItem = { ...fileItem, ...changes };
            const onChangeDispacher = this.onChangeDispatcherFactory(new Map(files));
            if (updatedFileItem.status === FileUploaderStatus.ABORTED) {
                files.delete(updatedFileItem.key);
            }
            else {
                files.set(updatedFileItem.key, updatedFileItem);
            }
            this.notififyFileItemStatus(updatedFileItem);
            this.setState({ files }, () => {
                this.validateDescription();
                onChangeDispacher();
            });
        };
        /**
         * Restart the upload of the given fileItem.
         *
         * @param fileItem the given fileItem.
         */
        this.onRetry = (fileItem) => {
            const { onRetry } = this.props;
            const changes = {
                percentage: 0,
                state: FileUploaderStatus.PENDING,
            };
            const updatedFileItem = { ...fileItem, ...changes };
            this.notififyFileItemStatus(updatedFileItem);
            this.startUpload(updatedFileItem);
            if (isFunction(onRetry)) {
                onRetry({ ...updatedFileItem });
            }
        };
        /**
         * The browsing property the given fileItem receives true and the state of the component is updated.
         *
         * @param fileItem the given fileItem.
         */
        this.onClickPredefinedItem = (fileItem) => {
            const { files } = this.state;
            const newFileItem = {
                ...fileItem,
                browsing: true,
            };
            files.set(newFileItem.key, newFileItem);
            this.setState({ files });
        };
        /**
         * Iterates over state.files property calling @see FileUploaderContent.resetFile for each file in the list.
         * Fires onBeforeClear and onClear.
         *
         * @returns Promise<boolean> that can be used to abort this operation.
         */
        this.onClearAllClick = () => {
            const { onBeforeClear, onClear } = this.props;
            const { files } = this.state;
            const abort = createAbort();
            if (isFunction(onBeforeClear)) {
                onBeforeClear(new AbortableClearEvent({ abort }));
            }
            return abort.promise.then(aborted => {
                if (aborted)
                    return true;
                files.forEach((fileItem) => this.resetFile(fileItem));
                if (isFunction(onClear)) {
                    onClear(new ClearEvent({}));
                }
                return false;
            });
        };
        /**
         * Modal's success action callback.
         *
         * @returns Promise<boolean> that can be used to abort this operation.
         */
        this.onModalSuccess = () => {
            const { onConfirmButtonClick, onRequiredFileError } = this.props;
            const hasMissingFiles = this.hasMissingFiles();
            if (hasMissingFiles) {
                this.updateRequiredFilesFromPedingToMissing();
            }
            if (isFunction(onRequiredFileError)) {
                onRequiredFileError(hasMissingFiles);
            }
            if (isFunction(onConfirmButtonClick)) {
                onConfirmButtonClick(hasMissingFiles);
            }
            return Promise.resolve(hasMissingFiles);
        };
        /**
         * Defines which modal buttons are going to be available.
         *
         * @returns The buttons definition
         */
        this.getModalButtons = () => {
            const { clearExpression = FileUploaderContent.defaultProps.clearExpression, confirmExpression = FileUploaderContent.defaultProps.confirmExpression, } = this.props;
            return [
                {
                    action: this.onClearAllClick,
                    title: clearExpression,
                    type: ModalButtonType.CANCEL,
                },
                {
                    action: this.onModalSuccess,
                    title: confirmExpression,
                    type: ModalButtonType.SUCCESS,
                },
            ];
        };
        /**
         * Creates a ReactElement.
         *
         * @returns ReactElement representing the FileUploaderContent in modal wrapper mode.
         */
        this.renderModalWrapper = () => {
            const { closeableModal, modalProps } = this.props;
            return (React.createElement(Modal, { ...modalProps, closeable: closeableModal, dockButtons: this.getModalButtons(), focusOnOpen: true, noPadding: isXSmallResolution(), onClose: this.onClose, type: ModalType.DEFAULT }, this.renderContextualBarWrapper()));
        };
        /**
         * Creates a ReactElement.
         *
         * @returns ReactElement representing the FileUploaderContent in no-wrapper mode.
         */
        this.renderNoWrapper = () => {
            const { predefinedFiles } = this.props;
            const predefined = predefinedFiles && predefinedFiles.length > 0;
            return (React.createElement(React.Fragment, null,
                predefined ? null : this.renderUploadArea(),
                this.renderFiles()));
        };
    }
    /**
     * Overrides Component.componentDidMount
     */
    componentDidMount() {
        this.buildStateFiles();
    }
    /**
     * Overrides Component.componentDidUpdate
     *
     * @param prevProps the given prevProps
     */
    componentDidUpdate(prevProps) {
        const { predefinedFiles = {}, value } = this.props;
        const { predefinedFiles: prevPredefinedFiles = {}, value: prevValue } = prevProps;
        if (!isEqualShallow(predefinedFiles, prevPredefinedFiles) || !isEqualShallow(value, prevValue)) {
            this.buildStateFiles();
        }
    }
    /**
     * Reads predefinedFiles and value from component's props to build the files map of the component's state.
     */
    buildStateFiles() {
        const { value = [], predefinedFiles = [], showMissingFiles = false } = this.props;
        const files = new Map();
        for (const predefinedFile of predefinedFiles) {
            files.set(predefinedFile.key, {
                key: predefinedFile.key,
                predefined: true,
                predefinedFile: predefinedFile,
                status: showMissingFiles && Boolean(predefinedFile.required) ? FileUploaderStatus.MISSING : FileUploaderStatus.PENDING,
            });
        }
        for (const file of value) {
            const currentFile = {
                description: file.description,
                descriptionTouched: true,
                file: {
                    name: file.filename,
                    size: file.size,
                    type: file.type,
                },
                key: file.predefinedFileKey ?? file.filename,
                path: file.filepath,
                percentage: 100,
                status: FileUploaderStatus.DONE,
            };
            const predefinedFile = files.get(file.predefinedFileKey ?? '');
            files.set(file.predefinedFileKey ?? file.filename, { ...predefinedFile, ...currentFile });
        }
        this.setState({ files });
    }
    /**
     * Reads allowedTypes from component's prop and join them in a comma separated string.
     *
     * @returns comma separated string representing the list of allowed types.
     */
    getAcceptedTypesString() {
        const { allowedTypes = [] } = this.props;
        return allowedTypes.join(', ');
    }
    /**
     * Reads predefinedFiles from component's prop, filter required ones, then map their keys to an array of strings.
     *
     * @returns array containg the keys of predefined files defined as required.
     */
    getRequiredFilesKeys() {
        const { predefinedFiles = [] } = this.props;
        return predefinedFiles.filter((file) => file.required).map(file => file.key);
    }
    /**
     * Removes or fixes invalid types definitions of the given list of types.
     *
     * @param allowedTypes the given list of types.
     * @returns array containing the normalized version of the given allowedType array.
     */
    normalizeAllowedTypes(allowedTypes) {
        return allowedTypes
            .map(type => type.trim())
            .filter(type => type.length > 0)
            .map(type => type.charAt(0) === '.' ? type : `.${type}`);
    }
    /**
     * Check the type of the given fileItem.
     *
     * @param fileName the given fileItem.
     * @returns true when the type of the given fileItem is allowed.
     */
    isAllowedType(fileName) {
        const { allowedTypes } = this.props;
        if (!allowedTypes || !allowedTypes.length) {
            return true;
        }
        const normalizedAllowedTypes = this.normalizeAllowedTypes(allowedTypes);
        const allowedTypesJoined = normalizedAllowedTypes?.join('|');
        const allowedTypesRegex = new RegExp(`(${allowedTypesJoined})$`);
        return Boolean(fileName.match(allowedTypesRegex));
    }
    /**
     * Check if the given fielName has already been uploaded.
     *
     * @param fileName the given fileName
     * @returns true when the given fileName already exists in the files property of the component's state
     */
    hasFile(fileName) {
        const { files } = this.state;
        return files.has(fileName);
    }
    /**
     * Reset the given fileItem to its initial state.
     * Predefined files are set to FileUploaderStatus.MISSING or FileUploaderStatus.PENDING.
     * Non Predefined files are removed from component's state.
     *
     * @param fileItem the given fileItem
     */
    resetFile(fileItem) {
        const { fileHandlerInstance, onRemove } = this.props;
        const { files } = this.state;
        const onChangeDispacher = this.onChangeDispatcherFactory(new Map(files));
        if (fileItem.predefined) {
            const file = {
                ...fileItem,
                file: undefined,
                path: undefined,
                percentage: 0,
                status: fileItem?.predefinedFile?.required ? FileUploaderStatus.MISSING : FileUploaderStatus.PENDING,
            };
            files.set(fileItem.key, file);
        }
        else {
            files.delete(fileItem.key);
        }
        if (fileItem.path && fileItem.file) {
            fileHandlerInstance.remove(fileItem.path, fileItem.file.name, () => {
                if (isFunction(onRemove)) {
                    onRemove({ path: fileItem.path || '', status: fileItem.status });
                }
            });
        }
        this.setState({ files }, () => {
            this.validateDescription();
            onChangeDispacher();
        });
    }
    /**
     * Maps the given fileItem to a BasicFileProp object.
     * @param fileItem the given fileItem
     *
     * @returns the resulting BasicFile object.
     */
    fileItemToBasicFile(fileItem) {
        return {
            description: fileItem.description,
            filename: fileItem.file?.name ?? '',
            filepath: fileItem.path ?? '',
            predefinedFileKey: fileItem.predefinedFile ? fileItem.predefinedFile.key : undefined,
            size: fileItem.file?.size ?? 0,
            type: fileItem.file?.type,
        };
    }
    /**
     * Checks if the given fileItem state is equal to FileUploaderStatus.DONE.
     *
     * @param fileItem the given fileItem.
     * @returns true when the state of the given fileItem is equal to FileUploaderStatus.DONE.
     */
    isFileItemDone(fileItem) {
        return fileItem.status == FileUploaderStatus.DONE;
    }
    /**
     * Checks if the files property in the current state and the given oldFiles are equal, if they are not equal, it builds and fires an onChange event.
     *
     * @param oldfiles the given oldFiles
     * @returns function to be called after updating the state of the component.
     */
    onChangeDispatcherFactory(oldfiles) {
        return () => {
            const { onChange } = this.props;
            const { files } = this.state;
            if (!isFunction(onChange)) {
                return;
            }
            const oldValue = Array.from(oldfiles.values())
                .filter(this.isFileItemDone)
                .map(this.fileItemToBasicFile);
            const value = Array.from(files.values())
                .filter(this.isFileItemDone)
                .map(this.fileItemToBasicFile);
            if (oldValue.length !== value.length || !oldValue.every((item, index) => isEqualShallow(item, value[index]))) {
                onChange(new ChangeEvent({ oldValue, value }));
            }
        };
    }
    /**
     * Notify observers by firing events (onError, onCompleted, onInvalid or onAbort) according
     * the given fileItem state.
     *
     * @param fileItem the given fileItem.
     */
    notififyFileItemStatus(fileItem) {
        const { onAbort, onCompleted, onError, onInvalid } = this.props;
        const { file, key, path, percentage, predefined, predefinedFile, status } = fileItem;
        if (status === FileUploaderStatus.ERROR && isFunction(onError)) {
            onError({ file, key, path, predefined, predefinedFile, progress: percentage, status });
        }
        if (status === FileUploaderStatus.DONE && isFunction(onCompleted)) {
            onCompleted({ file, key, path, predefined, predefinedFile, progress: percentage, status });
        }
        if (status === FileUploaderStatus.INVALID && isFunction(onInvalid)) {
            onInvalid({ file, key, path, predefined, predefinedFile, progress: percentage, status });
        }
        if (status === FileUploaderStatus.ABORTED && isFunction(onAbort)) {
            onAbort({ file, key, path, predefined, predefinedFile, progress: percentage, status });
        }
    }
    /**
     * Shows a ToastNotification given user feedback regarding the file type verificarion
     *
     * @param fileName the given file
     * @param allowedTypes list of allowed file types (comma-separated)
     */
    showNotAllowedTypeToast(fileName, allowedTypes) {
        ToastService.open({
            dismissible: false,
            highlight: true,
            id: 'file-uploader-modal-file-type-not-allowed',
            level: ToastLevel.ERROR,
            message: MessageService.translate('tws.file-uploader-modal.disallowed-file-type-toast-message', { allowedTypes, fileName }),
            placement: Placement.TOP,
            showIcon: true,
            title: 'tws.file-uploader-modal.disallowed-file-type-toast-title',
            type: ToastType.BUBBLE,
        });
    }
    /**
     * Uses FileService to send/uploade the given file to the backend service.
     *
     * @param file System file
     * @param key Link between the given file and the list of predefined files.
     */
    uploadPredefinedFile(file, fileItem) {
        const newFileItem = {
            ...fileItem,
            browsing: false,
            file: {
                name: file.name,
                size: file.size,
                systemFile: file,
                type: file.type,
            },
            status: FileUploaderStatus.LOAD,
        };
        this.uploadFileItem(newFileItem);
    }
    /**
     * Uses FileService to send/uploade the given file to the backend service.
     *
     * @param file System file
     */
    uploadFile(file) {
        if (this.hasFile(file.name)) {
            return;
        }
        const newFileItem = {
            file: {
                name: file.name,
                size: file.size,
                systemFile: file,
                type: file.type,
            },
            key: file.name,
            status: FileUploaderStatus.LOAD,
        };
        this.uploadFileItem(newFileItem);
    }
    /**
     * Iterats the given fileList degating each file to @see FileUploaderContent.uploadFile.
     *
     * @param files A list of system files
     */
    uploadFiles(fileList) {
        Array.from(fileList).forEach(file => this.uploadFile(file));
    }
    /**
     * Validates the type of given fileItem and starts the upload of it.
     * Delegates to @see FileUploaderContent.startUpload
     *
     * @param fileItem Logical representation of the file
     * @param file System file
     */
    uploadFileItem(fileItem) {
        const { onUpload } = this.props;
        if (!fileItem.file) {
            return;
        }
        if (!this.isAllowedType(fileItem.file.name)) {
            this.showNotAllowedTypeToast(fileItem.file.name, this.getAcceptedTypesString());
            return;
        }
        this.startUpload(fileItem);
        if (isFunction(onUpload)) {
            onUpload({ ...fileItem });
        }
    }
    /**
     * Starts the upload of the given fileItem.
     * Delegates the upload operation to @see FileService.upload.
     * Delegates the Upload progress feedback to @see FileUploaderContent.onProgress.
     *
     * @param fileItem The given fileItem.
     */
    startUpload(fileItem) {
        const { fileHandlerInstance, maxFileSize } = this.props;
        const uploadProgressHandler = (response) => this.onProgress(response, fileItem);
        if (!fileItem.file || !fileItem.file.systemFile) {
            return;
        }
        fileHandlerInstance.upload(fileItem.file.systemFile, { onProgress: uploadProgressHandler }, maxFileSize)
            .then(uploadProgressHandler)
            .catch(uploadProgressHandler);
    }
    /**
     * Checks for missing required files.
     *
     * @returns true when any required file has not been address.
     */
    hasMissingFiles() {
        const { files } = this.state;
        const requiredFiles = this.getRequiredFilesKeys();
        if (!requiredFiles.length) {
            return false;
        }
        return requiredFiles.some(fileKey => files.get(fileKey)?.status !== FileUploaderStatus.DONE);
    }
    /**
     * Update all required files in peding status (not done) to missing.
     */
    updateRequiredFilesFromPedingToMissing() {
        const { files } = this.state;
        const requiredFiles = this.getRequiredFilesKeys();
        const mappedFiles = new Map(files);
        for (const [key, value] of files) {
            if (requiredFiles.includes(key) && value.status !== FileUploaderStatus.DONE) {
                mappedFiles.set(key, { ...value, browsing: false, status: FileUploaderStatus.MISSING });
            }
        }
        this.setState({ files: mappedFiles });
    }
    /**
     * Updates the description of the given files with the given description, updates component's state and fire onChange event.
     *
     * @param fileItem the given file.
     * @param description the targer description.
      */
    updateFileItemDescription(fileItem, description) {
        const { files } = this.state;
        const updatedFileItem = { ...fileItem, description };
        const onChangeDispacher = this.onChangeDispatcherFactory(new Map(files));
        files.set(fileItem.key, updatedFileItem);
        this.setState({ files }, () => {
            this.validateDescription();
            onChangeDispacher();
        });
    }
    /**
     * Validates if a description was informaed for each file, when required
     */
    validateDescription() {
        const { descriptionRequired } = this.props;
        const { files } = this.state;
        if (!this.context || !descriptionRequired) {
            return;
        }
        const validations = [{
                message: 'tws.file-uploader.description-required.validator',
                name: 'file-uploader-description-required',
                valid: Array.from(files.values()).every(file => file.description),
            }];
        this.context.updateChildValidations(validations);
    }
    /**
     * Creates a ReactElement.
     *
     * @param fileItem the given fileItem.
     * @param index the given index.
     * @returns ReactElement representing the given fileItem.
     */
    renderFile(fileItem, index) {
        const { addDescriptionExpression, canAddDescription, descriptionPlaceholderExpression, failedUploadExpression = FileUploaderContent.defaultProps.failedUploadExpression, fileHandlerInstance = FileUploaderContent.defaultProps.fileHandlerInstance, maxFileSize, readOnly, descriptionRequired, valid, } = this.props;
        const title = fileItem.predefinedFile ? MessageService.translate(fileItem.predefinedFile.value) : fileItem.file?.name;
        const validFileItem = !valid && descriptionRequired ? Boolean(fileItem.description) || !fileItem.descriptionTouched : true;
        const path = fileItem.path ?? '/';
        /**
         * For the time being, child files are considered valid if they have description even when its parent (this) is not valid.
         */
        return (React.createElement(FileComponent, { addDescriptionExpression: addDescriptionExpression, canAddDescription: canAddDescription, description: fileItem.description, descriptionPlaceholderExpression: descriptionPlaceholderExpression, failedUploadExpression: failedUploadExpression, fileHandlerInstance: fileHandlerInstance, key: index, maxFileSize: maxFileSize, onBlur: () => this.onBlur(), onCancel: () => this.onCancel(fileItem), onChange: (event) => this.updateFileItemDescription(fileItem, event.value), onClear: () => this.resetFile(fileItem), onClickPredefinedItem: () => this.onClickPredefinedItem(fileItem), onFocus: () => this.onFocus(fileItem), onPreview: () => this.onPreview(path), onRemove: () => this.resetFile(fileItem), onRetry: () => this.onRetry(fileItem), path: path, predefined: fileItem.predefined, predefinedFile: fileItem.predefinedFile, progress: fileItem.percentage, readOnly: readOnly, required: Boolean(fileItem.predefinedFile?.required), size: fileItem.file?.size, status: fileItem.status, title: title, type: fileItem.file?.type, valid: validFileItem }));
    }
    /**
     * Creates a ReactElement.
     *
     * @param fileItem the given fileItem.
     * @param index the given index.
     * @returns ReactElement representing the given fileItem upload area.
     */
    renderUploadArea(fileItem, index) {
        const { browseFileExpression = FileUploaderContent.defaultProps.browseFileExpression, readOnly } = this.props;
        const onChange = (files) => {
            if (!files) {
                return;
            }
            if (fileItem) {
                this.uploadPredefinedFile(files[0], fileItem);
            }
            else {
                this.uploadFiles(files);
            }
        };
        return (React.createElement(UploadArea, { acceptedFileTypes: this.getAcceptedTypesString(), buttonTheme: ButtonTheme.SECONDARY, className: "c-file-uploader__input", key: index, multiple: !fileItem, onChange: onChange, readOnly: readOnly, value: browseFileExpression }));
    }
    /**
     * Creates a ReacElement.
     *
     * @returns ReactElement representing the files.
     */
    renderFiles() {
        const { files } = this.state;
        return (React.createElement("div", { className: "c-file-uploaded" },
            React.createElement("ul", null, [...files.values()].map((item, index) => ((!item.browsing)
                ? this.renderFile(item, index)
                : this.renderUploadArea(item, index))))));
    }
    /**
     * Creates a ReactElement.
     *
     * @returns ReactElement representing the FileUploaerContent in contextual-bar wrapper mode.
     */
    renderContextualBarWrapper() {
        return (React.createElement(ContextualBar, { title: "tws.file-uploader.add-file", uuid: "fake-uuid" },
            React.createElement("div", { className: "c-file-uploader-modal__container" }, this.renderNoWrapper())));
    }
    /**
     * Render the necessary actions for a given mode.
     */
    renderActions() {
        const { clearExpression = FileUploaderContent.defaultProps.clearExpression, confirmExpression = FileUploaderContent.defaultProps.confirmExpression, mode = FileUploaderContent.defaultProps.mode, readOnly, } = this.props;
        if (readOnly) {
            return null;
        }
        return (React.createElement(FileUploaderActions, { clearExpression: clearExpression, confirmExpression: confirmExpression, mode: mode, onClearClick: () => void this.onClearAllClick(), onConfirmClick: () => void this.onModalSuccess() }));
    }
    /**
     * Creates a ReactElement..
     *
     * @returns ReactElement representing the FileUploaderContent according its mode.
     */
    render() {
        const { mode } = this.props;
        const render = {
            [FileUploaderContentMode.CONTEXTUAL_BAR_WRAPPER]: () => this.renderContextualBarWrapper(),
            [FileUploaderContentMode.MODAL_WRAPPER]: () => this.renderModalWrapper(),
            [FileUploaderContentMode.NO_WRAPPER]: () => this.renderNoWrapper(),
        };
        return (React.createElement(Fragment, null,
            render[mode](),
            this.renderActions()));
    }
}
FileUploaderContent.contextType = FieldContext;
FileUploaderContent.propTypes = {
    addDescriptionExpression: expression,
    allowedTypes: list,
    browseFileExpression: expression,
    canAddDescription: bool,
    clearExpression: expression,
    closeableModal: bool,
    confirmExpression: expression,
    descriptionPlaceholderExpression: expression,
    descriptionRequired: bool,
    failedUploadExpression: expression,
    fileHandlerInstance: object.notEnumerable(),
    filesListTitleExpression: expression,
    maxFileSize: maxFileSizeShape,
    modalProps: extendComponent('Modal', [
        'closeByDocument',
        'closeByEscape',
    ], [
        'children',
        'className',
        'closeable',
        'closeByDocument',
        'closeByEscape',
        'container',
        'contextData',
        'dialogDescription',
        'dockButtons',
        'focusOnOpen',
        'fragment',
        'hideHeaderOnMobile',
        'noPadding',
        'onBeforeClose',
        'onCancel',
        'onClose',
        'onSubmit',
        'theme',
        'title',
        'type',
        'uuid',
    ]),
    mode: oneOf(enumValues(FileUploaderContentMode)).notEnumerable(),
    onAbort: eventListener,
    onBeforeClear: eventListener,
    onBeforeClose: eventListener,
    onBlur: eventListener,
    onChange: eventListener,
    onClear: eventListener,
    onClose: eventListener,
    onCompleted: eventListener,
    onError: eventListener,
    onFocus: eventListener,
    onInvalid: eventListener,
    onPreview: eventListener,
    onRemove: eventListener,
    onRetry: eventListener,
    onUpload: eventListener,
    /* eslint-disable-next-line sort-keys */
    predefinedFiles: record({ key: string, value: expression, required: bool }),
    readOnly: bool,
    valid: bool,
    value: arrayOf(shape({ filename: string, filepath: string, size: number })),
};
FileUploaderContent.defaultProps = {
    addDescriptionExpression: 'tws.file-uploader.add-description-expression',
    allowedTypes: [],
    browseFileExpression: 'tws.file-uploader.browse',
    canAddDescription: false,
    clearExpression: 'tws.file-uploader-modal.clear',
    closeableModal: true,
    confirmExpression: 'tws.file-uploader-modal.confirm',
    descriptionPlaceholderExpression: 'tws.file-uploader.description-placeholder-expression',
    descriptionRequired: false,
    failedUploadExpression: 'tws.file-uploader.failed',
    fileHandlerInstance: FileService.getFileHandler(),
    filesListTitleExpression: 'tws.file-uploader.files-list-title-expression',
    modalProps: {
        closeByDocument: true,
        closeByEscape: true,
    },
    mode: FileUploaderContentMode.NO_WRAPPER,
    readOnly: false,
    value: [],
};
export { FileUploaderContent, };
//# sourceMappingURL=file-uploader-content.js.map